// =============================================================================
// Hàm thuần của module KHOÁN: nhãn, số còn lại, cờ khai vượt, tháng / hạn khai
// theo giờ VN, cộng tiền của tôi, gom danh sách chờ duyệt, diễn giải lịch sử.
// Luật nằm ở DB (migration 20261102_0002/0003) — ở đây chỉ để HIỂN THỊ. Hàm nào
// chép luật SQL thì ghi "MIRROR" kèm tên hàm SQL: sửa 1 nơi phải sửa cả nơi kia.
// =============================================================================

import { formatVnd } from '@/features/orders/order-utils'
import { PERMISSIONS } from '@/lib/permissions'
import type {
  PieceworkAuditRow,
  PieceworkEntryKind,
  PieceworkEntryStatus,
  PieceworkMyAdjustment,
  PieceworkMyEntry,
  PieceworkOrderItem,
  PieceworkPendingEntry,
} from './piecework-types'

// Có 1 trong các quyền này → menu "Khoán" + route /piecework. Báo cáo khoán nằm ở
// /reports/piecework (REPORTS_HUB_PERMISSIONS), không cần quyền module.
export const PIECEWORK_MODULE_PERMISSIONS = [
  PERMISSIONS.PIECEWORK_DECLARE,
  PERMISSIONS.PIECEWORK_APPROVE,
  PERMISSIONS.PIECEWORK_CONFIG,
  PERMISSIONS.PIECEWORK_CLOSE_PERIOD,
] as const

export const ENTRY_STATUS_LABEL: Record<PieceworkEntryStatus, string> = {
  pending: 'Chờ duyệt',
  approved: 'Đã duyệt',
  rejected: 'Bị từ chối',
}

export const ENTRY_STATUS_VARIANT: Record<PieceworkEntryStatus, 'warning' | 'success' | 'danger'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'danger',
}

export const ENTRY_KIND_LABEL: Record<PieceworkEntryKind, string> = {
  self: 'Tự khai',
  proxy: 'Khai hộ',
  move: 'Chuyển số',
}

// Trần số bức 1 lần khai — khớp kiểm `p_qty > 100000` của piecework_declare.
export const MAX_QTY = 100000

// ---------------------------------------------------------------------------
// Số lượng
// ---------------------------------------------------------------------------

// Ô nhập số bức: số nguyên 1..MAX_QTY, cho phép khoảng trắng 2 đầu. Sai → null.
export function parseQtyInput(text: string): number | null {
  const s = text.trim()
  if (!/^\d+$/.test(s)) return null
  const n = Number(s)
  return n >= 1 && n <= MAX_QTY ? n : null
}

// Ô số trong hộp thoại: sàn `min`, trần `max` (null = không trần). parseQtyInput cố ý không nhận
// 0 (ô khai thường); riêng sàn 0 (giảm số, 0 = bỏ hẳn dòng) nhận thêm đúng số 0.
export function qtyOk(value: string, min: number, max: number | null): number | null {
  const qty = min === 0 && /^0+$/.test(value.trim()) ? 0 : parseQtyInput(value)
  if (qty == null || qty < min || (max != null && qty > max)) return null
  return qty
}

// ---------------------------------------------------------------------------
// Ngày giờ theo giờ VN (UTC+7, không có giờ mùa hè)
// ---------------------------------------------------------------------------

const VN_OFFSET_MS = 7 * 3_600_000
const pad2 = (n: number) => String(n).padStart(2, '0')

function vnParts(ms: number) {
  const d = new Date(ms + VN_OFFSET_MS)
  return {
    y: d.getUTCFullYear(),
    m: d.getUTCMonth() + 1,
    d: d.getUTCDate(),
    hh: d.getUTCHours(),
    mi: d.getUTCMinutes(),
  }
}

// 'YYYY-MM-DD' theo giờ VN.
export function vnDate(ms: number): string {
  const p = vnParts(ms)
  return `${p.y}-${pad2(p.m)}-${pad2(p.d)}`
}

// Kỳ 'YYYY-MM' theo giờ VN — khớp piecework_ym(piecework_vn_today()) ở DB.
export function vnYm(ms: number): string {
  const p = vnParts(ms)
  return `${p.y}-${pad2(p.m)}`
}

// Lùi / tiến n tháng: shiftYm('2026-01', -1) = '2025-12'.
export function shiftYm(ym: string, delta: number): string {
  const [y, m] = ym.split('-').map(Number)
  const idx = y! * 12 + (m! - 1) + delta
  return `${Math.floor(idx / 12)}-${pad2((idx % 12) + 1)}`
}

export function ymLabel(ym: string): string {
  const [y, m] = ym.split('-')
  return `Tháng ${Number(m)}/${y}`
}

// Ngày đầu + ngày cuối của tháng ('YYYY-MM-DD'), cho RPC báo cáo nhận date.
export function ymBounds(ym: string): { from: string; to: string } {
  const [y, m] = ym.split('-').map(Number)
  const last = new Date(Date.UTC(y!, m!, 0)).getUTCDate()
  return { from: `${ym}-01`, to: `${ym}-${pad2(last)}` }
}

// 'HH:mm dd/MM' theo giờ VN.
export function formatVnDayTime(iso: string): string {
  const p = vnParts(Date.parse(iso))
  return `${pad2(p.hh)}:${pad2(p.mi)} ${pad2(p.d)}/${pad2(p.m)}`
}

// ---------------------------------------------------------------------------
// Hạn khai
// ---------------------------------------------------------------------------

export type DeclareWindow = 'not_enrolled' | 'in_production' | 'grace' | 'closed'

type WindowInput = {
  // Danh sách đơn đang mở không có cờ này (mọi dòng đều đã đăng ký).
  enrolled?: boolean
  in_production: boolean | null
  declare_deadline: string | null
}

// MIRROR piecework_order_open (SQL): đang sản xuất, hoặc trước hạn khai. Tính lại
// theo `nowMs` để trang mở lâu vẫn tự khoá khi quá hạn (cờ `open` của RPC là lúc tải).
export function declareWindow(o: WindowInput, nowMs: number): DeclareWindow {
  if (o.enrolled === false) return 'not_enrolled'
  if (o.in_production) return 'in_production'
  if (o.declare_deadline && nowMs < Date.parse(o.declare_deadline)) return 'grace'
  return 'closed'
}

export function canDeclareNow(o: WindowInput, nowMs: number): boolean {
  const w = declareWindow(o, nowMs)
  return w === 'in_production' || w === 'grace'
}

export function declareWindowText(o: WindowInput, nowMs: number): string {
  switch (declareWindow(o, nowMs)) {
    case 'not_enrolled':
      return 'Đơn chưa tính khoán'
    case 'in_production':
      return 'Đang sản xuất — khai được'
    case 'grace': {
      // Hạn là 00:00 ngày kia → hiện 23:59 ngày mai cho dễ hiểu.
      const last = new Date(Date.parse(o.declare_deadline!) - 60_000).toISOString()
      return `Đã rời sản xuất — còn khai được tới ${formatVnDayTime(last)}`
    }
    case 'closed':
      return 'Đã hết hạn khai — nhờ quản lý khai hộ'
  }
}

// ---------------------------------------------------------------------------
// Tiền của tôi trong 1 tháng (piecework_my_summary trả dòng thô)
// ---------------------------------------------------------------------------

export type MyStageTotal = {
  stageId: number
  stageName: string
  approvedQty: number
  approvedAmount: number
  pendingQty: number
}

export type MyMonthTotals = {
  approvedQty: number
  approvedAmount: number
  pendingQty: number
  pendingAmount: number
  rejectedQty: number
  adjustmentsAmount: number
  // Tổng được nhận = đã duyệt + điều chỉnh. Dòng chờ duyệt CHƯA tính.
  payableAmount: number
  byStage: MyStageTotal[]
}

export function summarizeMyMonth(
  entries: ReadonlyArray<PieceworkMyEntry>,
  adjustments: ReadonlyArray<PieceworkMyAdjustment>,
): MyMonthTotals {
  const t = { approvedQty: 0, approvedAmount: 0, pendingQty: 0, pendingAmount: 0, rejectedQty: 0 }
  const byStage = new Map<number, MyStageTotal>()
  for (const e of entries) {
    const s = byStage.get(e.stage_id) ?? {
      stageId: e.stage_id,
      stageName: e.stage_name,
      approvedQty: 0,
      approvedAmount: 0,
      pendingQty: 0,
    }
    if (e.status === 'approved') {
      t.approvedQty += e.qty
      t.approvedAmount += e.amount
      s.approvedQty += e.qty
      s.approvedAmount += e.amount
    } else if (e.status === 'pending') {
      t.pendingQty += e.qty
      t.pendingAmount += e.amount
      s.pendingQty += e.qty
    } else {
      t.rejectedQty += e.qty
    }
    byStage.set(e.stage_id, s)
  }
  const adjustmentsAmount = adjustments.reduce((sum, a) => sum + a.amount, 0)
  return {
    ...t,
    adjustmentsAmount,
    payableAmount: t.approvedAmount + adjustmentsAmount,
    byStage: [...byStage.values()]
      .filter((s) => s.approvedQty > 0 || s.pendingQty > 0)
      .sort((a, b) => a.stageName.localeCompare(b.stageName, 'vi')),
  }
}

// ---------------------------------------------------------------------------
// Danh sách chờ duyệt: gom theo thợ → ngày (giữ thứ tự RPC: tên thợ, ngày, đơn)
// ---------------------------------------------------------------------------

export type PendingDay = { workDate: string; rows: PieceworkPendingEntry[] }

export type PendingGroup = {
  workerId: string
  workerName: string
  qty: number
  amount: number
  ids: number[]
  days: PendingDay[]
}

export function groupPendingByWorker(rows: ReadonlyArray<PieceworkPendingEntry>): PendingGroup[] {
  const groups: PendingGroup[] = []
  const byWorker = new Map<string, PendingGroup>()
  for (const r of rows) {
    let g = byWorker.get(r.worker_id)
    if (!g) {
      g = { workerId: r.worker_id, workerName: r.worker_name ?? '—', qty: 0, amount: 0, ids: [], days: [] }
      byWorker.set(r.worker_id, g)
      groups.push(g)
    }
    g.qty += r.qty
    g.amount += r.amount
    g.ids.push(r.id)
    let day = g.days.find((d) => d.workDate === r.work_date)
    if (!day) {
      day = { workDate: r.work_date, rows: [] }
      g.days.push(day)
    }
    day.rows.push(r)
  }
  return groups
}

// ---------------------------------------------------------------------------
// Cảnh báo dòng sản phẩm của 1 đơn
// ---------------------------------------------------------------------------

// noDmQty: số bức chưa gán loại định mức (không sinh việc). unconfigured: loại định
// mức có trên đơn mà chưa gán công đoạn đang dùng nào.
export function itemWarnings(items: ReadonlyArray<PieceworkOrderItem>): {
  noDmQty: number
  unconfigured: string[]
} {
  let noDmQty = 0
  const unconfigured = new Set<string>()
  for (const it of items) {
    if (!it.dm_code) noDmQty += it.quantity
    else if (!it.dm_configured) unconfigured.add(it.dm_code)
  }
  return { noDmQty, unconfigured: [...unconfigured].sort() }
}

// ---------------------------------------------------------------------------
// Diễn giải 1 dòng lịch sử (piecework_history) thành câu tiếng Việt
// ---------------------------------------------------------------------------

function num(v: unknown): number | null {
  if (typeof v === 'number') return v
  if (typeof v === 'string' && v.trim() !== '' && !Number.isNaN(Number(v))) return Number(v)
  return null
}

function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v : null
}

export function describeAuditRow(row: PieceworkAuditRow, stageNames: ReadonlyMap<number, string>): string {
  const before = row.old_row ?? {}
  const after = row.new_row ?? {}
  const cur = row.new_row ?? row.old_row ?? {}
  const stageId = num(cur.stage_id)
  const stage = stageId == null ? '' : (stageNames.get(stageId) ?? `công đoạn #${stageId}`)
  const who = row.worker_name ?? '—'
  switch (row.entity) {
    case 'entry': {
      const qty = num(cur.qty) ?? 0
      if (row.action === 'insert') {
        if (cur.kind === 'proxy') return `Khai hộ ${who}: ${qty} bức ${stage}`
        if (cur.kind === 'move') return `Chuyển ${qty} bức ${stage} sang ${who}`
        return `${who} khai ${qty} bức ${stage}`
      }
      if (row.action === 'delete') return `Xoá dòng khai ${qty} bức ${stage} của ${who}`
      if (before.status !== after.status) {
        if (after.status === 'approved') return `Duyệt ${qty} bức ${stage} của ${who}`
        if (after.status === 'rejected') {
          const why = str(after.reject_reason)
          return `Từ chối ${qty} bức ${stage} của ${who}${why ? `: ${why}` : ''}`
        }
      }
      if (before.piece_id !== after.piece_id) return `Đổi link sản phẩm, dòng khai ${stage} của ${who} đi theo bức mới`
      if (before.qty !== after.qty) {
        return `Sửa số bức ${stage} của ${who}: ${num(before.qty) ?? '?'} → ${num(after.qty) ?? '?'}`
      }
      return `Cập nhật dòng khai ${stage} của ${who}`
    }
    case 'dispute': {
      if (row.action === 'insert') {
        const note = str(cur.note)
        return `${who} báo sai: nhận ${num(cur.claimed_qty) ?? 0} bức${note ? ` — ${note}` : ''}`
      }
      if (after.status === 'resolved') return 'Báo sai đã xử lý'
      if (after.status === 'dismissed') return 'Báo sai: không đúng'
      return 'Cập nhật báo sai'
    }
    case 'task':
      return `Đã kiểm tra khai vượt ${stage} (cần làm ${num(after.review_ack_qty) ?? 0})`
    case 'review':
      return `Đã kiểm tra, giữ nguyên ${stage} (cần làm ${num(after.ack_qty) ?? 0})`
    case 'adjustment': {
      const amount = num(cur.amount) ?? 0
      const signed = `${amount > 0 ? '+' : ''}${formatVnd(amount)}`
      if (row.action === 'delete') return `Xoá điều chỉnh ${signed} của ${who}`
      const reason = str(cur.reason)
      return `Điều chỉnh ${signed} cho ${who}${reason ? `: ${reason}` : ''}`
    }
  }
}

// ---------------------------------------------------------------------------
// Bảng gán loại định mức × công đoạn (trang Cấu hình)
// ---------------------------------------------------------------------------

// Ô tick đã lưu, gom theo loại định mức (id công đoạn tăng dần).
export function linksByDm(links: ReadonlyArray<{ dm_code: string; stage_id: number }>): Map<string, number[]> {
  const m = new Map<string, number[]>()
  for (const l of links) {
    const ids = m.get(l.dm_code) ?? []
    ids.push(l.stage_id)
    m.set(l.dm_code, ids)
  }
  for (const ids of m.values()) ids.sort((a, b) => a - b)
  return m
}

// Bật / tắt 1 ô — trả mảng MỚI, tăng dần.
export function toggleId(ids: ReadonlyArray<number>, id: number): number[] {
  const next = ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id]
  return next.sort((a, b) => a - b)
}

export function sameIds(a: ReadonlyArray<number>, b: ReadonlyArray<number>): boolean {
  if (a.length !== b.length) return false
  const sa = [...a].sort((x, y) => x - y)
  const sb = [...b].sort((x, y) => x - y)
  return sa.every((v, i) => v === sb[i])
}

// Loại định mức "đã cấu hình" = có ÍT NHẤT 1 công đoạn ĐANG DÙNG được tick — khớp
// piecework_unconfigured_dm_usage / dm_configured ở SQL.
export function hasActiveStage(
  ids: ReadonlyArray<number>,
  stages: ReadonlyArray<{ id: number; is_active: boolean }>,
): boolean {
  const active = new Set(stages.filter((s) => s.is_active).map((s) => s.id))
  return ids.some((id) => active.has(id))
}
